import { forwardRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Message } from '@components/templates/bia/hooks/useStreamState';
import '@components/templates/bia/chat/markdown.css';
import { clsx } from 'clsx';
import { CardJobTalentMemo } from '@components/templates/job/Card/CardJobTalent';
import { TalentCardMemo } from '@components/templates/employer/cards-lists/TalentCard';
import { useExtendedRouter } from '@root/src/hooks/next-routing-wrappers';
import ChatCardsCarousel from './ChatCardsCarousel';

interface ChatMessageComponentProps {
  message: Message;
  locale: Locale;
  onInteraction?: (message: Message) => void;
}

export interface JobDataset {
  items: {
    id: string;
    score: number;
    data: ApiSchema<'JobItem'>
  }[]
}

const formatContent = (content: string): string[] => {
  let formattedContent = content.replace(/<br>/g, '\n\n');
  formattedContent = formattedContent.replace(/(\n)###/g, '\n\n###');
  return [formattedContent];
};

export const ChatMessageComponent = forwardRef<
HTMLDivElement,
ChatMessageComponentProps
>((props, ref) => {
  const { message, locale, onInteraction } = props;
  const { type, content } = message;
  const { withLocale } = useExtendedRouter();

  let jobItems = {} as JobDataset;

  const isUserMessage = type === 'human';

  if (message?.response_metadata?.customData?.jobSearchResults) {
    jobItems = message?.response_metadata?.customData?.jobSearchResults as unknown as JobDataset;
  }

  const formattedContent = !isUserMessage ? formatContent(content) : [content];

  const openTalentModal = useCallback((userSlug: string, as: string | undefined) => {
    onInteraction?.(message);
    // Set the navigation URL with the CV link
    window.history.pushState(null, '', withLocale(as ? `${as}?selectedCvSlug=${userSlug}` : ''));
  }, [withLocale, onInteraction, message]);

  return (
    <>
      {formattedContent.join('').trim() !== '' && (
        <div
          ref={ref}
          className={clsx(
            'flex flex-col gap-5',
            isUserMessage ? 'items-end' : 'items-start',
          )}
        >

          <div
            className={clsx(
              'markdown rounded-2xl px-4 py-2 text-sm',
              isUserMessage ? 'rounded-tl-2xl rounded-tr-sm bg-ink text-surface' : 'rounded-tl-sm rounded-tr-2xl bg-surface',
            )}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
              {formattedContent.join('')}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {jobItems?.items?.length > 0 && (
        <>
          <div className="relative hidden auto-rows-fr gap-4 md:grid-cols-2 lg:grid lg:grid-cols-3">
            {jobItems?.items?.map(((item, index) => (
              <CardJobTalentMemo
                key={item?.data?.slug}
                job={item?.data}
                locale={locale}
                position={index + 1}
                openInModal
                className="w-full"
              />
            )))}
          </div>
          <div
            className="block w-full lg:hidden"
          >
            <ChatCardsCarousel items={jobItems?.items?.map(((item, index) => (
              <CardJobTalentMemo
                key={item?.data?.slug}
                job={item?.data}
                locale={locale}
                position={index + 1}
                openInModal
                className="w-full"
              />
            )))}
            />
          </div>
        </>
      )}

      {
        message?.response_metadata?.customData?.resumeSearchResults?.items && message.response_metadata.customData.resumeSearchResults.items.length > 0 && (
          <>
            <div className="relative hidden auto-rows-fr gap-4 md:grid-cols-2 lg:grid lg:grid-cols-3">
              {message.response_metadata.customData.resumeSearchResults.items.map(((item) => (
                <TalentCardMemo
                  locale={locale}
                  key={item.slug}
                  cardData={item}
                  openTalentModal={openTalentModal}
                  className="w-full"
                />
              )))}
            </div>
            <div
              className="block w-full lg:hidden"
            >
              <ChatCardsCarousel items={message.response_metadata.customData.resumeSearchResults.items.map(((item) => (
                <TalentCardMemo
                  locale={locale}
                  key={item.slug}
                  cardData={item}
                  openTalentModal={openTalentModal}
                  className="w-full"
                />
              )))}
              />
            </div>
          </>
        )
      }
    </>
  );
});

ChatMessageComponent.displayName = 'ChatMessageBubbleAI';
